import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { collection, addDoc, query, orderBy, limit, onSnapshot, serverTimestamp, doc, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import ThreadService from '../services/ThreadService';

const ThreadGenerator = ({ onVideoGenerate, user }) => {
  const [tweetUrl, setTweetUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [threadData, setThreadData] = useState(null);
  const [showFullThread, setShowFullThread] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [savedThreads, setSavedThreads] = useState([]);
  const [showThreadHistory, setShowThreadHistory] = useState(false);
  const tweetRef = useRef(null);

  // User profile state for username validation
  const [userProfile, setUserProfile] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingUsername, setPendingUsername] = useState('');

  // Sample mode state
  const [showSampleConfirmation, setShowSampleConfirmation] = useState(false);
  const [pendingSampleUsername, setPendingSampleUsername] = useState('');

  // Duplicate video detection state
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [pendingDuplicateUrl, setPendingDuplicateUrl] = useState('');

  // Audio selection state (copied from TwitterImporter)
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [selectedAudioType, setSelectedAudioType] = useState('default'); // 'default', 'custom', 'library'
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [selectedLibraryAudio, setSelectedLibraryAudio] = useState('');
  const [customAudioFile, setCustomAudioFile] = useState(null);
  const [availableLibraryAudio, setAvailableLibraryAudio] = useState([]);
  const [userCustomAudio, setUserCustomAudio] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('trending');
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [starredAudio, setStarredAudio] = useState([]);
  const audioRef = useRef(null);

  const threadService = new ThreadService();

  // Load user profile from Firebase
  useEffect(() => {
    if (!user) return;

    // Subscribe to user's profile document
    const userProfileRef = doc(db, 'users', user.uid);
    
    const unsubscribe = onSnapshot(userProfileRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setUserProfile(data);
      } else {
        // Initialize user profile if it doesn't exist
        const initialProfile = {
          totalVideos: 0,
          recentImports: [],
          twitterUsernames: [],
          sampleUsernames: [],
          isUnlimited: false,
          createdAt: new Date().toISOString()
        };
        setDoc(userProfileRef, initialProfile);
        setUserProfile(initialProfile);
      }
    });

    return () => unsubscribe();
  }, [user]);

  // Load user's custom audio library (copied from TwitterImporter)
  useEffect(() => {
    if (!user) return;

    const customAudioRef = collection(db, 'users', user.uid, 'customAudio');
    const q = query(customAudioRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const customAudioList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        isCustom: true
      }));
      setUserCustomAudio(customAudioList);
    });

    return () => unsubscribe();
  }, [user]);

  // Load available library audio files with categories (copied from TwitterImporter)
  useEffect(() => {
    const loadLibraryAudio = async () => {
      try {
        setAvailableLibraryAudio([
          // Trending
          { id: 'grave', name: 'GRAVE', filename: 'GRAVE.MP3', category: 'trending' },
          { id: 'wush_2', name: 'WUSH 2', filename: 'WUSH 2.mp3', category: 'trending' },
          { id: 'promises', name: 'PROMISES', filename: 'PROMISES.mp3', category: 'trending' },
          // { id: 'flip_flop_fliep_flap', name: 'Flip Flop Fliep Flap', filename: 'Flip Flop Fliep Flap.mp3', category: 'trending' },
          // { id: 'no_other_demo_heart', name: 'No Other Demo Heart', filename: 'No Other Demo Heart.mp3', category: 'trending' },
          // { id: 'somebody', name: 'Somebody', filename: 'Somebody.mp3', category: 'trending' },
          // { id: 'believe', name: 'Believe', filename: 'Believe.mp3', category: 'trending' },
          // { id: 'doomsday', name: 'Doomsday', filename: 'doomsday.mp3', category: 'trending' },
          // { id: 'seteadora', name: 'SETEADORA', filename: 'SETEADORA.mp3', category: 'trending' },
          // { id: 'rules', name: 'Rules', filename: 'Rules.mp3', category: 'trending' },
          // { id: 'el_tiki_ta', name: 'El Tiki-Ta', filename: 'El Tiki-Ta.mp3', category: 'trending' },
          // { id: 'la_boda', name: 'La Boda', filename: 'La Boda.mp3', category: 'trending' },
          // { id: 'unslept', name: 'Unslept', filename: 'Unslept.mp3', category: 'trending' },
          // { id: 'i_know', name: 'I Know', filename: 'I Know.mp3', category: 'trending' },
          // { id: 'last_kiss', name: 'Last Kiss', filename: 'Last Kiss.mp3', category: 'trending' },
          // { id: 'neon_guts', name: 'Neon Guts', filename: 'Neon Guts.mp3', category: 'trending' },
          // { id: 'piano_tiles', name: 'Piano Tiles', filename: 'Piano Tiles.mp3', category: 'trending' },
          // { id: 'yellow_cab', name: 'Yellow Cab', filename: 'Yellow Cab.mp3', category: 'trending' },
          // { id: 'cant_go_broke_remix', name: 'Cant Go Broke (Remix)', filename: 'Cant Go Broke (Remix).mp3', category: 'trending' },
          // { id: 'amnesia', name: 'Amnesia', filename: 'Amnesia.mp3', category: 'trending' },
          // { id: 'cant_stop_the_feeling', name: "CAN'T STOP THE FEELING!", filename: "CAN'T STOP THE FEELING!.mp3", category: 'trending' },
          // { id: 'my_own_hymn', name: 'My Own Hymn', filename: 'My Own Hymn.mp3', category: 'trending' },
          // { id: 'сердце', name: 'Сердце', filename: 'Сердце.mp3', category: 'trending' },
          
          // Chill
          // { id: 'all_i_need', name: 'ALL I NEED', filename: 'ALL I NEED.mp3', category: 'chill' },
          // { id: 'all_10', name: 'ALL 10', filename: 'ALL 10.mp3', category: 'chill' },
          // { id: 'void_in_blue', name: 'VOID IN BLUE', filename: 'VOID IN BLUE.mp3', category: 'chill' },
          // { id: 'horizonte_de_diamente', name: 'HORIZONTE DE DIAMENTE', filename: 'HORIZONTE DE DIAMENTE.mp3', category: 'chill' },
          // { id: 'horizonte_ultra_slowed', name: 'HORIZONTE ULTRA SLOWED', filename: 'HORIZONTE ULTRA SLOWED.mp3', category: 'chill' },
          // { id: 'nostalgia', name: 'NOSTALGIA', filename: 'NOSTALGIA.mp3', category: 'chill' },
          // { id: 'dream', name: 'DREAM', filename: 'DREAM.mp3', category: 'chill' },
          
          // Hype
          // { id: 'spaciship', name: 'SPACISHIP', filename: 'SPACISHIP.mp3', category: 'hype' },
          // { id: 'stay_schemmin', name: 'STAY SCHEMMIN', filename: 'STAY SCHEMMIN.mp3', category: 'hype' },
          // { id: 'on_that_bxtch', name: 'ON THAT BXTCH!', filename: 'ON THAT BXTCH!mp3.mp3', category: 'hype' },
          // { id: 'fkngas', name: 'FKNGAS', filename: 'FKNGAS.mp3', category: 'hype' },
          // { id: 'fkniggrs_song', name: 'FKNIGGRS SONG', filename: 'FKNIGGRS SONG.mp3', category: 'hype' },
          // { id: 'emo_slut', name: 'EMO SLUT', filename: 'EMO SLUT.mp3', category: 'hype' },
          // { id: 'montagem_bilao', name: 'MONTAGEM BILAO', filename: 'MONTAGEM BILAO.mp3', category: 'hype' },
          // { id: 'evil_tiktok_audio', name: 'EVIL TIKTOK AUDIO', filename: 'EVIL TIKTOK AUDIO.mp3', category: 'hype' },
          // { id: 'jay_guapo', name: 'JAY GUAPO', filename: 'JAY GUAPO.mp3', category: 'hype' },
          // { id: 'montagem_indeferenca', name: 'MONTAGEM INDEFERENCA', filename: 'MONTAGEM INDEFERENCA.mp3', category: 'hype' },
          // { id: 'number_19', name: '#19', filename: '#19.mp3', category: 'hype' },
          // Vibes
          // { id: 'funk_mi_camino', name: 'FUNK MI CAMINO', filename: 'FUNK MI CAMINO.mp3', category: 'vibes' },
          // { id: 'magic', name: 'MAGIC', filename: 'MAGIC.mp3', category: 'vibes' },
          // { id: 'magic_x_maybe_for_you', name: 'MAGIC x MAYBE FOR YOU', filename: 'MAGIC x MAYBE FOR YOU.mp3', category: 'vibes' },
          // { id: 'magic_2', name: 'MAGIC 2', filename: 'MAGIC 2.mp3', category: 'vibes' },
          // { id: 'maybe_for_you', name: 'MAYBE FOR YOU', filename: 'MAYBE FOR YOU.mp3', category: 'vibes' },
          // { id: 'mrs_magic', name: 'MRS. MAGIC', filename: 'MRS. MAGIC.mp3', category: 'vibes' },
          // { id: 'mac_demarco', name: 'MAC DEMARCO', filename: 'MAC DEMARCO.mp3', category: 'vibes' },
          // { id: 'anotherlife', name: 'ANOTHERLIFE', filename: 'ANOTHERLIFE.mp3', category: 'vibes' },
          // { id: 'dream', name: 'DREAM', filename: 'DREAM.mp3', category: 'vibes' },
          // { id: 'dream_2', name: 'DREAM 2', filename: 'DREAM 2.mp3', category: 'vibes' },
          // { id: 'vision', name: 'VISION', filename: 'VISION.mp3', category: 'vibes' },
          // { id: 'gigimason_x_cali', name: 'GIGIMASON x CALI', filename: 'GIGIMASON x CALI.mp3', category: 'vibes' },
          // { id: 'gta_theme', name: 'GTA THEME', filename: 'GTA THEME.mp3', category: 'vibes' },
          // { id: 'we_we', name: 'WE WE', filename: 'WE WE.mp3', category: 'vibes' },
        ]);
      } catch (error) {
        console.error('Error loading library audio:', error);
      }
    };
    
    loadLibraryAudio();
  }, []);

  // Load user's starred audio preferences (copied from TwitterImporter)
  useEffect(() => {
    if (!user) return;

    const loadStarredAudio = async () => {
      try {
        const audioPrefsRef = doc(db, 'users', user.uid, 'audioPreferences', 'starred');
        const audioPrefsDoc = await getDoc(audioPrefsRef);
        
        if (audioPrefsDoc.exists()) {
          const starredIds = audioPrefsDoc.data().audioIds || [];
          setStarredAudio(starredIds);
        } else {
          setStarredAudio([]);
        }
      } catch (error) {
        console.error('Error loading starred audio:', error);
        setStarredAudio([]);
      }
    };

    loadStarredAudio();
  }, [user]);

  // stop audio when modal is closed (copied from TwitterImporter)
  useEffect(() => {
    if (!showAudioModal && audioRef.current) {
        audioRef.current.pause();
        setPlayingAudioId(null);
    }
  }, [showAudioModal]);

  // Load saved threads from Firebase
  useEffect(() => {
    if (!user?.uid) return;

    const threadsRef = collection(db, 'users', user.uid, 'threads');
    const q = query(threadsRef, orderBy('createdAt', 'desc'), limit(10));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const threads = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSavedThreads(threads);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Extract tweet ID from URL
  const extractTweetId = (url) => {
    const match = url.match(/\/status\/(\d+)/);
    return match ? match[1] : null;
  };

  // Extract username from Twitter URL
  const extractUsernameFromUrl = (url) => {
    const patterns = [
      /twitter\.com\/(\w+)\/status\/\d+/,
      /x\.com\/(\w+)\/status\/\d+/,
      /mobile\.twitter\.com\/(\w+)\/status\/\d+/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Add user's Twitter username to their allowed list
  const setUserTwitterUsername = async (username) => {
    if (!user || !username) return;

    const userProfileRef = doc(db, 'users', user.uid);
    
    // Get current usernames and add new one if not already present
    const currentUsernames = userProfile?.twitterUsernames || [];
    if (!currentUsernames.some(u => u.toLowerCase() === username.toLowerCase())) {
      const updatedUsernames = [...currentUsernames, username];
      await updateDoc(userProfileRef, {
        twitterUsernames: updatedUsernames,
        lockedAt: new Date().toISOString()
      });
    }
  };

  // Add user's sample username to track usage
  const addSampleUsername = async (username) => {
    if (!user || !username) return;

    const userProfileRef = doc(db, 'users', user.uid);
    
    // Get current sample usernames and add new one if not already present
    const currentSampleUsernames = userProfile?.sampleUsernames || [];
    if (!currentSampleUsernames.some(u => u.toLowerCase() === username.toLowerCase())) {
      const updatedSampleUsernames = [...currentSampleUsernames, username];
      await updateDoc(userProfileRef, {
        sampleUsernames: updatedSampleUsernames,
        lastSampleAt: new Date().toISOString()
      });
    }
  };

  // Check if username has been used for sample
  const hasUsedSample = (username) => {
    const sampleUsernames = userProfile?.sampleUsernames || [];
    return sampleUsernames.some(u => u.toLowerCase() === username.toLowerCase());
  };

  // Check if URL has been used before and return count
  const checkForDuplicateVideo = async (tweetUrl) => {
    if (!user) return 0;

    try {
      const allVideosRef = doc(db, 'users', user.uid, 'allVideos', 'videoList');
      const videosDoc = await getDoc(allVideosRef);
      
      if (!videosDoc.exists()) return 0;
      
      const allVideos = videosDoc.data().videos || [];
      const duplicateCount = allVideos.filter(video => video.url === tweetUrl).length;
      
      return duplicateCount;
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      return 0;
    }
  };

  // Log video generation to Firebase (stores ALL videos, not just recent)
  const logVideoGeneration = async (tweetUrl) => {
    if (!user) return;

    try {
      const allVideosRef = doc(db, 'users', user.uid, 'allVideos', 'videoList');
      const videosDoc = await getDoc(allVideosRef);
      
      const newVideoEntry = {
        url: tweetUrl,
        timestamp: new Date().toISOString(),
        createdAt: Date.now() // Use timestamp number instead of serverTimestamp() in array
      };

      if (videosDoc.exists()) {
        const currentVideos = videosDoc.data().videos || [];
        const updatedVideos = [newVideoEntry, ...currentVideos];
        
        await updateDoc(allVideosRef, {
          videos: updatedVideos,
          lastUpdated: serverTimestamp()
        });
      } else {
        await setDoc(allVideosRef, {
          videos: [newVideoEntry],
          lastUpdated: serverTimestamp()
        });
      }
      
      console.log('Video generation logged to Firebase:', tweetUrl);
    } catch (error) {
      console.error('Error logging video generation:', error);
    }
  };

  // Confirmation functions
  const confirmUsernameRestriction = async () => {
    console.log('User confirmed restriction to username:', pendingUsername);
    setShowConfirmation(false);
    await setUserTwitterUsername(pendingUsername);
    await proceedWithThreadFetch();
  };

  const cancelUsernameRestriction = () => {
    console.log('User cancelled username restriction');
    setShowConfirmation(false);
    setPendingUsername('');
  };

  // Sample mode confirmation functions
  const confirmSampleGeneration = async () => {
    console.log('User confirmed sample generation for username:', pendingSampleUsername);
    setShowSampleConfirmation(false);
    await addSampleUsername(pendingSampleUsername);
    await proceedWithThreadFetch();
  };

  const cancelSampleGeneration = () => {
    console.log('User cancelled sample generation');
    setShowSampleConfirmation(false);
    setPendingSampleUsername('');
  };

  // Duplicate warning confirmation functions
  const confirmDuplicateGeneration = async () => {
    console.log('User confirmed duplicate generation for:', pendingDuplicateUrl);
    setShowDuplicateWarning(false);
    setPendingDuplicateUrl('');
    setDuplicateCount(0);
    
    // Continue with the original thread fetch flow
    const username = extractUsernameFromUrl(tweetUrl);
    if (!username) {
      setError('Invalid Twitter URL format');
      return;
    }

    // Check if user can use this username
    if (userProfile && userProfile.twitterUsernames?.length > 0 && !userProfile.isUnlimited) {
      const hasAccess = userProfile.twitterUsernames.some(u => u.toLowerCase() === username.toLowerCase());
      if (!hasAccess) {
        if (hasUsedSample(username)) {
          setError(`You've already used your free sample for @${username}. You can only make videos from @${userProfile.twitterUsernames.join(', @')}.`);
          return;
        }
        setPendingSampleUsername(username);
        setShowSampleConfirmation(true);
        return;
      }
      await proceedWithThreadFetch();
      return;
    }

    if (userProfile && (!userProfile.twitterUsernames || userProfile.twitterUsernames.length === 0) && !userProfile.isUnlimited) {
      setPendingUsername(username);
      setShowConfirmation(true);
      return;
    }

    await proceedWithThreadFetch();
  };

  const cancelDuplicateGeneration = () => {
    console.log('User cancelled duplicate generation');
    setShowDuplicateWarning(false);
    setPendingDuplicateUrl('');
    setDuplicateCount(0);
  };

  // Save thread to Firebase
  const saveThreadToFirebase = async (threadData, tweetUrl) => {
    if (!user?.uid || !threadData?.tweets?.length) return;

    try {
      const firstTweet = threadData.tweets[0];
      const threadSummary = {
        url: tweetUrl,
        tweetId: firstTweet.id,
        author: {
          name: firstTweet.author.name,
          username: firstTweet.author.username,
          profilePicture: firstTweet.author.profilePicture,
          verified: firstTweet.author.isVerified || firstTweet.author.isBlueVerified
        },
        firstTweetText: firstTweet.text,
        tweetCount: threadData.tweets.length,
        totalMetrics: threadData.threadStats,
        createdAt: serverTimestamp()
      };

      const threadsRef = collection(db, 'users', user.uid, 'threads');
      await addDoc(threadsRef, threadSummary);
      
    } catch (error) {
      console.error('❌ Error saving thread to Firebase:', error);
    }
  };

  // Fetch thread data with username validation
  const handleFetchThread = async () => {
    if (!tweetUrl.trim()) {
      setError('Please enter a tweet URL');
      return;
    }

    // Log video generation immediately when fetch is clicked
    await logVideoGeneration(tweetUrl);

    // Check for duplicate videos
    const duplicates = await checkForDuplicateVideo(tweetUrl);
    if (duplicates > 1) { // > 1 because we just logged this one
      setDuplicateCount(duplicates - 1); // Don't count the one we just added
      setPendingDuplicateUrl(tweetUrl);
      setShowDuplicateWarning(true);
      return;
    }

    // Extract username from URL FIRST (before any API calls)
    const username = extractUsernameFromUrl(tweetUrl);
    if (!username) {
      setError('Invalid Twitter URL format');
      return;
    }

    console.log('Extracted username from URL:', username);
    console.log('User profile:', userProfile);

    // Check if user can use this username
    if (userProfile && userProfile.twitterUsernames?.length > 0 && !userProfile.isUnlimited) {
      // User already has locked usernames, check if this username is in the list
      const hasAccess = userProfile.twitterUsernames.some(u => u.toLowerCase() === username.toLowerCase());
      if (!hasAccess) {
        // Check if this username has already been used for a sample
        if (hasUsedSample(username)) {
          setError(`You've already used your free sample for @${username}. You can only make videos from @${userProfile.twitterUsernames.join(', @')}.`);
          return;
        }
        // Show sample mode option
        console.log('Showing sample confirmation for username:', username);
        setPendingSampleUsername(username);
        setShowSampleConfirmation(true);
        return;
      }
      // Username is in allowed list, proceed directly
      await proceedWithThreadFetch();
      return;
    }

    // If user hasn't set any Twitter usernames yet (and not unlimited), show confirmation
    if (userProfile && (!userProfile.twitterUsernames || userProfile.twitterUsernames.length === 0) && !userProfile.isUnlimited) {
      console.log('Showing confirmation for username:', username);
      setPendingUsername(username);
      setShowConfirmation(true);
      return;
    }

    // If user is unlimited or has no restrictions, proceed directly
    await proceedWithThreadFetch();
  };

  // Actual thread fetching logic (separated for reuse)
  const proceedWithThreadFetch = async () => {
    const tweetId = extractTweetId(tweetUrl);
    if (!tweetId) {
      setError('Invalid Twitter URL format');
      return;
    }

    setIsLoading(true);
    setError('');
    setThreadData(null);

    try {
      const result = await threadService.fetchCompleteThread(tweetId);

      if (result.success) {
        setThreadData(result);
        
        // Save to Firebase
        await saveThreadToFirebase(result, tweetUrl);
      } else {
        setError(result.error || 'Failed to fetch thread');
      }
    } catch (error) {
      console.error('❌ Thread fetch error:', error);
      setError('Failed to fetch thread data');
    } finally {
      setIsLoading(false);
    }
  };

  // Load a thread from history
  const loadThreadFromHistory = (savedThread) => {
    setTweetUrl(savedThread.url);
    setShowThreadHistory(false);
    // You could also auto-fetch the thread here if needed
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return time.toLocaleDateString();
  };

  // Copy thread text to clipboard
  const copyThreadText = () => {
    if (!threadData || !threadData.tweets) return;
    
    const threadText = threadService.generateThreadText(threadData.tweets);
    navigator.clipboard.writeText(threadText).then(() => {
      // Show success feedback
      const button = document.querySelector('.copy-thread-button');
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✅ Copied!';
        setTimeout(() => {
          button.textContent = originalText;
        }, 2000);
      }
    }).catch(() => {
      setError('Failed to copy thread text');
    });
  };

  // Generate video from thread (using just the first tweet)
  const generateVideoFromThread = async () => {
    if (!threadData || !threadData.tweets || threadData.tweets.length === 0) {
      setError('No thread data available');
      return;
    }

    setIsGeneratingVideo(true);
    setError('');

    try {
      const firstTweet = threadData.tweets[0];
      
      // Convert profile picture URL to File object for consistency with TweetGenerator
      const profileFile = await urlToFile(firstTweet.author.profilePicture, 'profile.jpg');
      
      // Create temporary tweet data (same structure as TweetGenerator)
      const allLibraryAudio = [...userCustomAudio, ...availableLibraryAudio];
      // Prepare audio selection data (same logic as TwitterImporter)
      const audioSelection = {
        type: selectedAudioType,
        file: selectedAudioType === 'custom' ? customAudioFile : null,
        libraryFilename: null,
        libraryUrl: null
      };
      
      if (selectedAudioType === 'library' && selectedLibraryAudio) {
        const audio = allLibraryAudio.find(a => a.id === selectedLibraryAudio);
        if (audio) {
            if (audio.isCustom) {
                audioSelection.libraryUrl = audio.url;
            } else {
                audioSelection.libraryFilename = audio.filename;
            }
        }
      }

      const tempTweetData = {
        name: firstTweet.author.name,
        username: firstTweet.author.username,
        message: firstTweet.text,
        theme: 'dark', // Force dark theme for viral effect
        verified: firstTweet.author.isVerified || firstTweet.author.isBlueVerified,
        profileFile: profileFile,
        mediaUrls: firstTweet.mediaUrls || [], // Add media URLs from thread data
        audioSelection: audioSelection // Add audio selection
      };

      // Wait a bit for the component to render
      await new Promise(resolve => setTimeout(resolve, 100));

      if (tweetRef.current) {
        const canvas = await html2canvas(tweetRef.current, {
          allowTaint: true,
          backgroundColor: '#000000',
          useCORS: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: document.documentElement.offsetWidth,
          windowHeight: document.documentElement.offsetHeight,
        });
        
        const imageData = canvas.toDataURL();
        
        // Pass the data to generate video (same pattern as TweetGenerator)
        onVideoGenerate({
          imageData,
          tweetData: {
            ...tempTweetData,
            // Add thread data for video library
            isThread: true,
            threadData: {
              tweets: threadData.tweets,
              threadStats: threadData.threadStats,
              url: tweetUrl
            }
          }
        });
      }
    } catch (error) {
      console.error('Error generating video from thread:', error);
      setError('Failed to generate video');
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  // Helper function to convert URL to File object
  const urlToFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type });
    } catch (error) {
      console.error('Error converting URL to file:', error);
      return null;
    }
  };

  // Helper to decode HTML entities (same as TweetGenerator)
  const decodeHTMLEntities = (text) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = text;
    return txt.value;
  };

  // Render message with highlights and better line break handling for Twitter API text
  const renderMessage = (message) => {
    if (!message) return 'Tweet content';

    // Enhanced preprocessing for Twitter API text formatting
    let processedMessage = message;
    
    // Handle various line break patterns that might come from Twitter API
    processedMessage = processedMessage.replace(/\r\n/g, '\n'); // Windows line breaks
    processedMessage = processedMessage.replace(/\r/g, '\n'); // Mac line breaks
    
    // Handle numbered list patterns (common in threads)
    processedMessage = processedMessage.replace(/(\d+\.\s)/g, '\n$1');
    
    // Handle bullet point patterns
    processedMessage = processedMessage.replace(/([•·▪▫‣⁃]\s)/g, '\n$1');
    
    // Handle paragraph breaks (double spaces often indicate intended line breaks)
    processedMessage = processedMessage.replace(/\s{2,}/g, '\n');
    
    // Clean up multiple consecutive newlines
    processedMessage = processedMessage.replace(/\n{3,}/g, '\n\n');
    
    // Trim leading/trailing whitespace and newlines
    processedMessage = processedMessage.trim();

    const lines = processedMessage.split('\n');
    
    return lines.map((line, lineIndex) => {
      const tokens = line.split(' ');
      const processedTokens = tokens.map((token, tokenIndex) => {
        if (token.match(/^@(\w){1,20}$/)) {
          return <span key={`${lineIndex}-${tokenIndex}`} className="highlight">{token}</span>;
        } else if (token.match(/^@(\w){21,}$/)) {
          return (
            <span key={`${lineIndex}-${tokenIndex}`}>
              <span className="highlight">{token.slice(0, 21)}</span>
              {token.slice(21)}
            </span>
          );
        } else if (token.match(/^@\w+/)) {
          const match = token.match(/^@\w+/)[0];
          const rest = token.replace(match, '');
          return (
            <span key={`${lineIndex}-${tokenIndex}`}>
              <span className="highlight">{match}</span>
              {rest}
            </span>
          );
        } else {
          return <span key={`${lineIndex}-${tokenIndex}`}>{token}</span>;
        }
      });

      return (
        <span key={lineIndex}>
          {processedTokens.reduce((acc, token, index) => {
            if (index === 0) return [token];
            return [...acc, ' ', token];
          }, [])}
          {lineIndex < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  // Audio-related functions (copied from TwitterImporter)
  const toggleStarredAudio = async (audioId) => {
    if (!user) return;

    try {
      const isCurrentlyStarred = starredAudio.includes(audioId);
      let newStarredAudio;

      if (isCurrentlyStarred) {
        // Remove from starred
        newStarredAudio = starredAudio.filter(id => id !== audioId);
      } else {
        // Add to starred (check limit)
        if (starredAudio.length >= 3) {
          setError('You can only star up to 3 audio tracks. Please unstar one first.');
          return;
        }
        newStarredAudio = [...starredAudio, audioId];
      }

      // Update Firebase
      const audioPrefsRef = doc(db, 'users', user.uid, 'audioPreferences', 'starred');
      await setDoc(audioPrefsRef, {
        audioIds: newStarredAudio,
        updatedAt: serverTimestamp()
      }, { merge: true });

      // Update local state
      setStarredAudio(newStarredAudio);
      
    } catch (error) {
      console.error('Error toggling starred audio:', error);
      setError('Failed to update starred audio. Please try again.');
    }
  };

  const handleAudioSelect = (type, file = null, libraryId = '') => {
    console.log('Audio selection changed:', { type, file, libraryId });
    
    setSelectedAudioType(type);
    
    if (type === 'custom' && file) {
      setCustomAudioFile(file);
      setSelectedAudioFile(file);
      setSelectedLibraryAudio('');
    } else if (type === 'library' && libraryId) {
      setSelectedLibraryAudio(libraryId);
      setSelectedAudioFile(null);
      setCustomAudioFile(null);
      
      // Log the selected library audio
      const selectedAudio = availableLibraryAudio.find(a => a.id === libraryId);
      console.log('Selected library audio:', selectedAudio);
    } else {
      // Default
      setSelectedAudioFile(null);
      setCustomAudioFile(null);
      setSelectedLibraryAudio('');
    }
  };

  const handleCustomAudioUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('audio/')) {
        setError('Please select a valid audio file (MP3, WAV, etc.)');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Audio file must be less than 10MB');
        return;
      }
      
      if (!user) {
        setError('You must be logged in to upload audio.');
        return;
      }

      setIsUploading(true);
      setError('');

      try {
        // Create a unique filename to avoid conflicts
        const timestamp = Date.now();
        const fileExtension = file.name.split('.').pop();
        const uniqueFilename = `custom_audio_${timestamp}.${fileExtension}`;
        
        const storageRef = ref(storage, `audio/${user.uid}/${uniqueFilename}`);
        const uploadTask = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(uploadTask.ref);

        // Save to Firestore
        const customAudioRef = collection(db, 'users', user.uid, 'customAudio');
        const docRef = await addDoc(customAudioRef, {
            name: file.name,
            originalName: file.name,
            url: downloadURL,
            filename: uniqueFilename,
            size: file.size,
            type: file.type,
            createdAt: serverTimestamp(),
        });

        // Update local state
        setUserCustomAudio(prev => [...prev, {
          id: docRef.id,
          name: file.name,
          url: downloadURL,
          filename: uniqueFilename,
          createdAt: new Date()
        }]);

        // Set as selected audio
        setSelectedAudioType('custom');
        setCustomAudioFile(file);
        setSelectedAudioFile(file);
        
        console.log('Custom audio uploaded successfully:', {
          name: file.name,
          size: file.size,
          url: downloadURL
        });

      } catch (error) {
          console.error("Error uploading audio:", error);
          setError("Failed to upload custom audio. Please try again.");
      } finally {
          setIsUploading(false);
          event.target.value = null;
      }
    }
  };

  // Delete custom audio from Firestore and Storage
  const handleDeleteCustomAudio = async (audioId, filename) => {
    if (!user || !audioId) return;

    try {
      // Show confirmation dialog
      if (!window.confirm('Are you sure you want to delete this audio file? This action cannot be undone.')) {
        return;
      }

      // Delete from Firestore
      const audioDocRef = doc(db, 'users', user.uid, 'customAudio', audioId);
      await deleteDoc(audioDocRef);

      // Delete from Storage
      if (filename) {
        const storageRef = ref(storage, `audio/${user.uid}/${filename}`);
        try {
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn('Storage file may not exist:', storageError);
          // Continue even if storage deletion fails (file might not exist)
        }
      }

      // Update local state
      setUserCustomAudio(prev => prev.filter(audio => audio.id !== audioId));

      // If the deleted audio was currently selected, clear the selection
      if (selectedAudioType === 'library' && selectedLibraryAudio === audioId) {
        setSelectedAudioType('');
        setSelectedLibraryAudio('');
      }

      console.log('Custom audio deleted successfully:', audioId);

    } catch (error) {
      console.error('Error deleting custom audio:', error);
      setError('Failed to delete audio file. Please try again.');
    }
  };

  const handleAudioConfirm = () => {
    // Keep the current selection and close the modal
    setShowAudioModal(false);
    
    // Log the final selection
    console.log('Audio selection confirmed:', {
      type: selectedAudioType,
      libraryId: selectedLibraryAudio,
      customFile: customAudioFile,
      selectedFile: selectedAudioFile
    });
  };

  const toggleAudioPreview = (e, audio) => {
    e.stopPropagation(); // Stop event from bubbling up to the radio button

    if (playingAudioId === audio.id) {
        // Pause current audio
        audioRef.current.pause();
        setPlayingAudioId(null);
    } else {
        // Stop currently playing audio if any
        if (audioRef.current) {
            audioRef.current.pause();
        }
        
        // Play new audio
        let audioPath;
        if (audio.isCustom) {
          audioPath = audio.url;
        } else {
          // Use correct Firebase Storage public URL for all library audio files
          const encodedFileName = encodeURIComponent(audio.filename);
          audioPath = `https://firebasestorage.googleapis.com/v0/b/twizz-f6d50.firebasestorage.app/o/audio%2F${encodedFileName}?alt=media`;
        }
        audioRef.current = new Audio(audioPath);
        audioRef.current.play().catch(err => {
          console.error("Failed to play audio:", err)
          setError("Could not play audio preview.")
        });

        setPlayingAudioId(audio.id);
        
        audioRef.current.onended = () => {
            setPlayingAudioId(null);
        };
    }
  };

  const getCategorizedAudio = () => {
    const categories = {
      trending: availableLibraryAudio.filter(audio => audio.category === 'trending'),
      chill: availableLibraryAudio.filter(audio => audio.category === 'chill'),
      hype: availableLibraryAudio.filter(audio => audio.category === 'hype'),
      vibes: availableLibraryAudio.filter(audio => audio.category === 'vibes'),
    };
    return categories;
  };

  const handleLibrarySelect = (audioId) => {
    setSelectedLibraryAudio(audioId);
    setSelectedAudioType('library');
    setSelectedAudioFile(null);
    setCustomAudioFile(null);
    setShowLibraryModal(false);
  };

  return (
    <div className="dashboard-twitter-importer">
      {/* Username Confirmation Modal */}
      {showConfirmation && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>🔗 Link Your Account</h3>
            </div>
            <div className="modal-body">
              <p>You'll be linked to this Twitter account:</p>
              <div className="username-highlight">
                @{pendingUsername}
              </div>
              <p className="info-text">
                This helps us provide a better experience and prevents abuse.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={cancelUsernameRestriction}
              >
                Cancel
              </button>
              <button 
                className="btn-upgrade"
                onClick={() => {
                  // Placeholder for upgrade functionality
                  alert('Upgrade to Pro feature coming soon!');
                }}
              >
                Upgrade to Pro
              </button>
              <button 
                className="btn-confirm"
                onClick={confirmUsernameRestriction}
              >
                Link Account & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sample Confirmation Modal */}
      {showSampleConfirmation && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>🎬 Generate One Sample</h3>
            </div>
            <div className="modal-body">
              <p>Generate your one free sample video from:</p>
              <div className="username-highlight">
                @{pendingSampleUsername}
              </div>
              <p className="info-text">
                After this sample, you won't be able to create more videos from this account. To create unlimited videos from any account, upgrade to Pro.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={cancelSampleGeneration}
              >
                Cancel
              </button>
              <button 
                className="btn-upgrade"
                onClick={() => {
                  // Placeholder for upgrade functionality
                  alert('Upgrade to Pro feature coming soon!');
                }}
              >
                Upgrade to Pro
              </button>
              <button 
                className="btn-confirm"
                onClick={confirmSampleGeneration}
              >
                Generate Sample
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Warning Modal */}
      {showDuplicateWarning && (
        <div className="confirmation-modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>⚠️ Duplicate Video Detected</h3>
            </div>
            <div className="modal-body">
              <p>You've already made this video <strong>{duplicateCount} time{duplicateCount !== 1 ? 's' : ''}</strong>. Are you sure you want to proceed?</p>
              <div className="username-highlight" style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                wordBreak: 'break-all'
              }}>
                {pendingDuplicateUrl}
              </div>
              <p className="info-text">
                This might be a duplicate tweet or a previous import.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={cancelDuplicateGeneration}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm"
                onClick={confirmDuplicateGeneration}
              >
                Yes, Generate Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audio Selection Modal */}
      {showAudioModal && (
        <div className="audio-modal-overlay" onClick={() => setShowAudioModal(false)}>
          <div className="audio-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🎵 Choose Audio</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAudioModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="audio-options">
              {/* Default Audio */}
              <div 
                className={`audio-option ${selectedAudioType === 'default' ? 'selected' : ''}`}
                onClick={() => handleAudioSelect('default')}
              >
                <div className="audio-icon">🎵</div>
                <div className="audio-details">
                  <div className="audio-name">Default - GRAVE</div>
                  <div className="audio-description">Our signature background music</div>
                </div>
                <div className="audio-radio">
                  <input 
                    type="radio" 
                    name="audio-selection" 
                    checked={selectedAudioType === 'default'}
                    onChange={() => handleAudioSelect('default')}
                  />
                </div>
              </div>

              {/* Starred Audio Quick Select */}
              {starredAudio.length > 0 && (
                <div className="audio-section">
                  <div className="section-title">⭐ Your Favorites ({starredAudio.length}/3)</div>
                  {starredAudio.map((audioId) => {
                    const audio = [...availableLibraryAudio, ...userCustomAudio].find(a => a.id === audioId);
                    if (!audio) return null;
                    
                    return (
                      <div 
                        key={audio.id}
                        className={`audio-option ${selectedAudioType === 'library' && selectedLibraryAudio === audio.id ? 'selected' : ''}`}
                        onClick={() => handleAudioSelect('library', null, audio.id)}
                      >
                        <button
                          className="audio-play-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAudioPreview(e, audio);
                          }}
                        >
                          {playingAudioId === audio.id ? '⏸' : '▶'}
                        </button>
                        <div className="audio-details">
                          <div className="audio-name">⭐ {audio.name}</div>
                          <div className="audio-description">Your starred favorite</div>
                        </div>
                        <div className="audio-radio">
                          <input 
                            type="radio" 
                            name="audio-selection" 
                            checked={selectedAudioType === 'library' && selectedLibraryAudio === audio.id}
                            onChange={() => handleAudioSelect('library', null, audio.id)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* User's Custom Audio Library */}
              {userCustomAudio.length > 0 && (
                <div className="audio-section">
                  <div className="section-title">✨ Your Library</div>
                  {userCustomAudio.map((audio) => (
                    <div
                      key={audio.id}
                      className={`audio-option ${selectedAudioType === 'library' && selectedLibraryAudio === audio.id ? 'selected' : ''}`}
                      onClick={() => handleAudioSelect('library', null, audio.id)}
                    >
                      <button
                        className="audio-play-button"
                        onClick={(e) => toggleAudioPreview(e, audio)}
                      >
                        {playingAudioId === audio.id ? '⏸' : '▶'}
                      </button>
                      <div className="audio-details">
                        <div className="audio-name">{audio.name}</div>
                        <div className="audio-description">Custom uploaded audio</div>
                      </div>
                      <button
                        className={`audio-star-button ${starredAudio.includes(audio.id) ? 'starred' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStarredAudio(audio.id);
                        }}
                        title={starredAudio.includes(audio.id) ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {starredAudio.includes(audio.id) ? '⭐' : '☆'}
                      </button>
                      <button
                        className="audio-delete-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCustomAudio(audio.id, audio.filename);
                        }}
                        title="Delete this audio file"
                      >
                        🗑️
                      </button>
                      <div className="audio-radio">
                        <input
                          type="radio"
                          name="audio-selection"
                          checked={selectedAudioType === 'library' && selectedLibraryAudio === audio.id}
                          onChange={() => handleAudioSelect('library', null, audio.id)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Custom Upload */}
              <div className={`audio-option ${selectedAudioType === 'custom' ? 'selected' : ''}`}>
                <div className="audio-icon">📎</div>
                <div className="audio-details">
                  <div className="audio-name">Upload Custom Audio</div>
                  <div className="audio-description">
                    Upload your own audio file (max 10MB)
                    {customAudioFile && (
                      <div className="custom-file-info">
                        📎 {customAudioFile.name}
                      </div>
                    )}
                  </div>
                </div>
                <div className="audio-action">
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={handleCustomAudioUpload}
                    style={{ display: 'none' }}
                    id="custom-audio-upload"
                    disabled={isUploading}
                  />
                  <label htmlFor="custom-audio-upload" className={`upload-btn ${isUploading ? 'uploading' : ''}`}>
                    {isUploading ? 'Uploading...' : 'Choose File'}
                  </label>
                </div>
              </div>

              {/* Library Audio - Choose from Library Button */}
              <div className="audio-section">
                <div className="section-title">🎶 Audio Library</div>
                <div 
                  className={`audio-option library-choose-button ${selectedAudioType === 'library' && selectedLibraryAudio ? 'selected' : ''}`}
                  onClick={() => setShowLibraryModal(true)}
                >
                  <div className="audio-icon">🎶</div>
                  <div className="audio-details">
                    <div className="audio-name">
                      {selectedLibraryAudio ? 
                        availableLibraryAudio.find(a => a.id === selectedLibraryAudio)?.name || 'Choose from Library' 
                        : 'Choose from Library'
                      }
                    </div>
                    <div className="audio-description">
                      {selectedLibraryAudio ? 'Library audio selected' : 'Browse categorized audio tracks'}
                    </div>
                  </div>
                  <div className="audio-arrow">→</div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => {
                  // Reset to previous selection
                  setShowAudioModal(false);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm"
                onClick={handleAudioConfirm}
              >
                Select Audio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Library Audio Modal */}
      {/* Library Selection Modal */}
      {showLibraryModal && (
        <div className="audio-modal-overlay" onClick={() => setShowLibraryModal(false)}>
          <div className="audio-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🎶 Choose from Library</h3>
              <button 
                className="modal-close"
                onClick={() => setShowLibraryModal(false)}
              >
                ×
              </button>
            </div>
            
            {/* Category Tabs */}
            <div className="category-tabs">
              {Object.entries({
                trending: '🔥 Trending',
                chill: '😌 Chill',
                hype: '⚡ Hype',
                vibes: '🌊 Vibes'
              }).map(([key, label]) => (
                <button
                  key={key}
                  className={`category-tab ${selectedCategory === key ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            
            {/* Categorized Audio */}
            <div className="categorized-audio">
              {getCategorizedAudio()[selectedCategory]?.map((audio) => (
                <div 
                  key={audio.id}
                  className={`audio-option ${selectedLibraryAudio === audio.id ? 'selected' : ''}`}
                  onClick={() => handleLibrarySelect(audio.id)}
                >
                  <button 
                    className="audio-play-button"
                    onClick={(e) => toggleAudioPreview(e, audio)}
                  >
                    {playingAudioId === audio.id ? '⏸' : '▶'}
                  </button>
                  <div className="audio-details">
                    <div className="audio-name">{audio.name}</div>
                    <div className="audio-description">Library audio track</div>
                  </div>
                  <button
                    className={`audio-star-button ${starredAudio.includes(audio.id) ? 'starred' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStarredAudio(audio.id);
                    }}
                    title={starredAudio.includes(audio.id) ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {starredAudio.includes(audio.id) ? '⭐' : '☆'}
                  </button>
                  <div className="audio-radio">
                    <input 
                      type="radio" 
                      name="library-selection" 
                      checked={selectedLibraryAudio === audio.id}
                      onChange={() => handleLibrarySelect(audio.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        <div className="content-header">
          <h1 className="page-title">🧵 Thread Mode</h1>
          <p className="page-description">Fetch complete Twitter threads and generate videos</p>
        </div>

        <div className="import-section">
          <div className="section-card">
            <div className="card-header">
              <h3 className="card-title">🔗 Import Thread</h3>
              <p className="card-description">Paste any Twitter/X thread URL to get started</p>
              
              {/* Show current account status */}
              {userProfile && (userProfile.isUnlimited || (userProfile.twitterUsernames && userProfile.twitterUsernames.length > 0)) && (
                <div className="account-status">
                  {userProfile.isUnlimited ? (
                    <div className="status-badge pro">
                      ✨ Pro Account - Unlimited Access
                    </div>
                  ) : (
                    <div className="status-badge linked">
                      🔗 Linked to @{userProfile.twitterUsernames.join(', @')}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="import-form">
              <div className="form-group">
                <label className="form-label">Thread URL</label>
                <div className="input-group">
                  <input
                    type="text"
                    value={tweetUrl}
                    onChange={(e) => setTweetUrl(e.target.value)}
                    placeholder="https://twitter.com/username/status/1234567890"
                    className="form-input"
                    disabled={isLoading}
                  />
                  
                  {/* Audio Selection Button */}
                  <button
                    className="audio-button"
                    onClick={() => setShowAudioModal(true)}
                    disabled={isLoading}
                    title="Choose Audio"
                  >
                    <span className="audio-icon">🎵</span>
                  </button>
                  
                  <button
                    onClick={handleFetchThread}
                    disabled={isLoading || !tweetUrl.trim()}
                    className={`import-button ${isLoading ? 'loading' : ''}`}
                  >
                    {isLoading ? (
                      <>
                        <span className="button-spinner"></span>
                        Fetching...
                      </>
                    ) : (
                      <>
                        <span className="button-icon">🧵</span>
                        Fetch Thread
                      </>
                    )}
                  </button>
                </div>
                
                {/* Audio Selection Display */}
                {selectedAudioType !== 'default' && (
                  <div className="audio-selection-display">
                    <span className="audio-indicator">
                      🎵 Audio: {
                        selectedAudioType === 'custom' 
                          ? customAudioFile?.name || 'Custom Upload'
                          : selectedAudioType === 'library' 
                            ? availableLibraryAudio.find(a => a.id === selectedLibraryAudio)?.name || 'Library Audio'
                            : 'Default - GRAVE'
                      }
                    </span>
                    <button 
                      className="change-audio-btn"
                      onClick={() => setShowAudioModal(true)}
                    >
                      Change
                    </button>
                  </div>
                )}
                
                {error && (
                  <div className="error-message">
                    <span className="error-icon">❌</span>
                    {error}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {threadData && (
          <div className="thread-results-section">
            <div className="section-card">
              <div className="card-header">
                <h3 className="card-title">🎯 Thread Results</h3>
                <p className="card-description">
                  Found {threadData.threadStats.totalTweets} tweets from @{threadData.tweets[0].author.username}
                </p>
              </div>

              <div className="thread-summary">
                <div className="thread-author">
                  <img 
                    src={threadData.tweets[0].author.profilePicture} 
                    alt={threadData.tweets[0].author.name}
                    className="author-avatar"
                  />
                  <div className="author-info">
                    <div className="author-name">
                      {threadData.tweets[0].author.name}
                      {(threadData.tweets[0].author.isVerified || threadData.tweets[0].author.isBlueVerified) && (
                        <svg className="verified-icon" width="16" height="16" viewBox="0 0 24 24" fill="#1d9bf0">
                          <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"></path>
                        </svg>
                      )}
                    </div>
                    <div className="author-username">@{threadData.tweets[0].author.username}</div>
                  </div>
                  <div className="thread-stats">
                    <div className="stat">
                      <span className="stat-number">{threadData.threadStats.totalTweets}</span>
                      <span className="stat-label">tweets</span>
                    </div>
                    <div className="stat">
                      <span className="stat-number">{threadData.threadStats.totalLikes.toLocaleString()}</span>
                      <span className="stat-label">likes</span>
                    </div>
                    <div className="stat">
                      <span className="stat-number">{threadData.threadStats.totalRetweets.toLocaleString()}</span>
                      <span className="stat-label">retweets</span>
                    </div>
                  </div>
                </div>

                <div className="thread-preview">
                  <h4 className="preview-title">Thread Preview</h4>
                  <div className="tweet-list">
                    {threadData.tweets.slice(0, showFullThread ? threadData.tweets.length : 3).map((tweet, index) => (
                      <div key={tweet.id} className="tweet-snippet">
                        <div className="tweet-number">{index + 1}/{threadData.tweets.length}</div>
                        <div className="tweet-text">{tweet.text}</div>
                        <div className="tweet-metrics">
                          <span>❤️ {tweet.metrics.likeCount.toLocaleString()}</span>
                          <span>🔄 {tweet.metrics.retweetCount.toLocaleString()}</span>
                          <span>💬 {tweet.metrics.replyCount.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {threadData.tweets.length > 3 && (
                    <button 
                      onClick={() => setShowFullThread(!showFullThread)}
                      className="show-more-btn"
                    >
                      {showFullThread ? '🔼 Show Less' : `🔽 Show All ${threadData.tweets.length} Tweets`}
                    </button>
                  )}
                </div>

                <div className="thread-actions">
                  <button
                    onClick={copyThreadText}
                    className="action-button secondary"
                  >
                    📋 Copy Thread Text
                  </button>
                  <button
                    onClick={generateVideoFromThread}
                    className="action-button primary"
                    disabled={isGeneratingVideo}
                  >
                    {isGeneratingVideo ? (
                      <>
                        <span className="button-spinner"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        🎬 Generate Video
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Thread History Section */}
        <div className="history-section">
          <div className="section-card">
            <div className="card-header">
              <h3 className="card-title">📋 Recent Threads</h3>
              <p className="card-description">Your recently fetched threads</p>
            </div>
            
            <div className="recent-list">
              {savedThreads.length > 0 ? (
                savedThreads.map((thread, index) => (
                  <div key={thread.id} className="recent-item">
                    <div className="recent-icon">🧵</div>
                    <div className="recent-content">
                      <div className="recent-thread-info">
                        <div className="thread-author-name">
                          {thread.author?.name || 'Unknown Author'}
                          {thread.author?.verified && <span className="verified-mini">✓</span>}
                        </div>
                        <div className="thread-snippet">
                          {thread.firstTweetText?.slice(0, 100) || 'No content available'}
                          {thread.firstTweetText?.length > 100 ? '...' : ''}
                        </div>
                        <div className="thread-meta">
                          {thread.tweetCount || 0} tweets • {formatTimeAgo(thread.createdAt)}
                        </div>
                      </div>
                    </div>
                    <button 
                      className="recent-action" 
                      title="Load this thread"
                      onClick={() => loadThreadFromHistory(thread)}
                    >
                      ↻
                    </button>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <p>No recent threads yet. Fetch your first thread to see it here!</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Hidden tweet element for screenshot */}
        {threadData && threadData.tweets.length > 0 && (
          <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
            <div ref={tweetRef} className="tweet dark" style={{ 
              width: '500px', 
              padding: '1.5rem', 
              background: '#000000', 
              color: '#ffffff' 
            }}>
              <div className="head">
                <div className="title">
                  <img
                    src={threadData.tweets[0].author.profilePicture}
                    alt="avatar"
                    width="48"
                    height="48"
                    style={{ borderRadius: '50%', objectFit: 'cover' }}
                  />
                  <div className="text">
                    <div className="top">
                      <span className="tweet_name" style={{ fontWeight: '700', fontSize: '0.95rem' }}>
                        {threadData.tweets[0].author.name}
                      </span>
                      {(threadData.tweets[0].author.isVerified || threadData.tweets[0].author.isBlueVerified) && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="verified"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="#1d9bf0"
                        >
                          <g>
                            <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"></path>
                          </g>
                        </svg>
                      )}
                    </div>
                    <div className="bottom" style={{ fontSize: '0.85rem', opacity: '0.7' }}>
                      @{threadData.tweets[0].author.username}
                    </div>
                  </div>
                </div>
                <div className="dots" style={{ opacity: '0.7' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
                    <g>
                      <circle cx="5" cy="12" r="2"></circle>
                      <circle cx="12" cy="12" r="2"></circle>
                      <circle cx="19" cy="12" r="2"></circle>
                    </g>
                  </svg>
                </div>
              </div>
              <div className="content" style={{ marginTop: '0.5rem' }}>
                <div className="message" style={{ fontSize: '1.1rem', lineHeight: '1.4', wordWrap: 'break-word' }}>
                  {renderMessage(decodeHTMLEntities(threadData.tweets[0].text))}
                  <br />
                  <br />
                  (Read Caption ⬇️)
                </div>
                
                {/* Image handling - copied from TweetGenerator */}
                {threadData.tweets[0].mediaUrls && threadData.tweets[0].mediaUrls.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    {threadData.tweets[0].mediaUrls.length === 1 ? (
                      <img
                        src={threadData.tweets[0].mediaUrls[0]}
                        alt="tweet media"
                        style={{ 
                          width: '100%', 
                          borderRadius: '16px',
                          objectFit: 'contain',
                          height: 'auto',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <div style={{
                        display: 'grid',
                        gap: '2px',
                        width: '100%',
                        gridTemplateColumns: threadData.tweets[0].mediaUrls.length === 2 ? 'repeat(2, 1fr)' : 
                                             threadData.tweets[0].mediaUrls.length === 3 ? '2fr 1fr' : 
                                             'repeat(2, 1fr)',
                        gridTemplateRows: threadData.tweets[0].mediaUrls.length === 2 ? '1fr' :
                                          threadData.tweets[0].mediaUrls.length === 3 ? 'repeat(2, 1fr)' :
                                          'repeat(2, 1fr)',
                        aspectRatio: threadData.tweets[0].mediaUrls.length === 2 ? '2/1' : '2/1',
                        borderRadius: '16px',
                        overflow: 'hidden',
                      }}>
                        {threadData.tweets[0].mediaUrls.map((url, index) => (
                          <div
                            key={index}
                            style={{
                              gridColumn: threadData.tweets[0].mediaUrls.length === 3 && index === 0 ? 'span 1' : 'span 1',
                              gridRow: threadData.tweets[0].mediaUrls.length === 3 && index === 0 ? 'span 2' : 'span 1',
                              overflow: 'hidden',
                              position: 'relative',
                              aspectRatio: '1/1',
                            }}
                          >
                            <img
                              src={url}
                              alt={`tweet media ${index + 1}`}
                              style={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '100%',
                                height: 'auto',
                                minHeight: '100%',
                                maxWidth: 'none',
                              }}
                              onLoad={(e) => {
                                const img = e.target;
                                const container = img.parentElement;
                                const containerWidth = container.offsetWidth;
                                const containerHeight = container.offsetHeight;
                                
                                // Calculate the scale to make width fit container
                                const scaleToFitWidth = containerWidth / img.naturalWidth;
                                const scaledHeight = img.naturalHeight * scaleToFitWidth;
                                
                                if (scaledHeight >= containerHeight) {
                                  // Image is tall enough, use width-based scaling
                                  img.style.width = '100%';
                                  img.style.height = 'auto';
                                  img.style.left = '50%';
                                  img.style.top = '50%';
                                  img.style.transform = 'translate(-50%, -50%)';
                                } else {
                                  // Image is too short, use height-based scaling
                                  img.style.width = 'auto';
                                  img.style.height = '100%';
                                  img.style.left = '50%';
                                  img.style.top = '50%';
                                  img.style.transform = 'translate(-50%, -50%)';
                                }
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreadGenerator; 